import { Add, Delete, Edit, MailOutline, Person, Warning } from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";

export default function FranchiseRole({ snackbar }) {
    const apiUrl = process.env.REACT_APP_API;

    // ─── State ────────────────────────────────────────────────────────────────
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(""); // "add" | "edit" | "delete"
    const [action, setAction] = useState(""); // "add" | "update" | "delete"

    const emptyForm = { fl_name: "", fl_email: "", fl_status: "active", fl_role: "" };
    const [form, setForm] = useState(emptyForm);
    const [selectedRole, setSelectedRole] = useState(null);

    // ─── Fetch all roles ──────────────────────────────────────────────────────
    const getRoles = () => {
        setLoading(true);
        axios
            .get(`${apiUrl}/franchise/roles/getall`)
            .then((res) => {
                if (res.data.status === true) {
                    setRoles(res.data.data);
                } else {
                    setRoles([]);
                }
            })
            .catch(() => setRoles([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        getRoles();
    }, []);

    // ─── Add ──────────────────────────────────────────────────────────────────
    const openAdd = () => {
        setForm(emptyForm);
        setOpen("add");
    };

    const handleAdd = () => {
        setAction("add");
        axios
            .post(`${apiUrl}/franchise/roles/add`, form, {
                headers: { "Content-Type": "application/json" },
            })
            .then((res) => {
                snackbar && snackbar(res.data.message || "Role added");
                if (res.data.status === true) {
                    setOpen("");
                    getRoles();
                }
            })
            .finally(() => setAction(""));
    };

    // ─── Edit ─────────────────────────────────────────────────────────────────
    const openEdit = (role) => {
        setSelectedRole(role);
        setForm({
            fl_name: role.fl_name,
            fl_email: role.fl_email,
            fl_status: role.fl_status,
            fl_role: role.fl_role || "",
        });
        setOpen("edit");
    };

    const handleUpdate = () => {
        setAction("update");
        axios
            .post(
                `${apiUrl}/franchise/roles/update`,
                { fl_id: selectedRole.fl_id, ...form },
                { headers: { "Content-Type": "application/json" } }
            )
            .then((res) => {
                snackbar && snackbar(res.data.message || "Role updated");
                if (res.data.status === true) {
                    setOpen("");
                    getRoles();
                }
            })
            .finally(() => setAction(""));
    };

    // ─── Delete ───────────────────────────────────────────────────────────────
    const openDelete = (role) => {
        setSelectedRole(role);
        setOpen("delete");
    };

    const handleDelete = () => {
        setAction("delete");
        axios
            .post(
                `${apiUrl}/franchise/roles/delete`,
                { fl_id: selectedRole.fl_id },
                { headers: { "Content-Type": "application/json" } }
            )
            .then((res) => {
                snackbar && snackbar(res.data.message || "Role deleted");
                if (res.data.status === true) {
                    setOpen("");
                    getRoles();
                }
            })
            .finally(() => setAction(""));
    };

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <Box className="content" pt={3} pb={5}>

            {/* ── Add Dialog ──────────────────────────────────────── */}
            <Dialog open={open === "add"} fullWidth maxWidth="xs" onClose={() => setOpen("")}>
                <DialogTitle>Add Franchise Role</DialogTitle>
                <Divider />
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <TextField
                            label="Name"
                            fullWidth
                            value={form.fl_name}
                            onChange={(e) => setForm({ ...form, fl_name: e.target.value })}
                        />
                        <TextField
                            label="Email"
                            fullWidth
                            type="email"
                            value={form.fl_email}
                            onChange={(e) => setForm({ ...form, fl_email: e.target.value })}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Role</InputLabel>
                            <Select
                                label="Role"
                                value={form.fl_role}
                                onChange={(e) => setForm({ ...form, fl_role: e.target.value })}
                            >
                                <MenuItem value="admin">Admin</MenuItem>
                                <MenuItem value="teacher">Teacher</MenuItem>
                                <MenuItem value="desk">Desk</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                label="Status"
                                value={form.fl_status}
                                onChange={(e) => setForm({ ...form, fl_status: e.target.value })}
                            >
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={1} mt={2}>
                        <Button
                            fullWidth
                            variant="contained"
                            disabled={action === "add"}
                            onClick={handleAdd}
                        >
                            {action === "add" ? <CircularProgress size={20} color="inherit" /> : "Add Role"}
                        </Button>
                        <Button fullWidth variant="outlined" color="inherit" onClick={() => setOpen("")}>
                            Cancel
                        </Button>
                    </Stack>
                </DialogContent>
            </Dialog>

            {/* ── Edit Dialog ─────────────────────────────────────── */}
            <Dialog open={open === "edit"} fullWidth maxWidth="xs" onClose={() => setOpen("")}>
                <DialogTitle>Edit Franchise Role</DialogTitle>
                <Divider />
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <TextField
                            label="Name"
                            fullWidth
                            value={form.fl_name}
                            onChange={(e) => setForm({ ...form, fl_name: e.target.value })}
                        />
                        <TextField
                            label="Email"
                            fullWidth
                            type="email"
                            value={form.fl_email}
                            onChange={(e) => setForm({ ...form, fl_email: e.target.value })}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Role</InputLabel>
                            <Select
                                label="Role"
                                value={form.fl_role}
                                onChange={(e) => setForm({ ...form, fl_role: e.target.value })}
                            >
                                <MenuItem value="admin">Admin</MenuItem>
                                <MenuItem value="teacher">Teacher</MenuItem>
                                <MenuItem value="desk">Desk</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                label="Status"
                                value={form.fl_status}
                                onChange={(e) => setForm({ ...form, fl_status: e.target.value })}
                            >
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={1} mt={2}>
                        <Button
                            fullWidth
                            variant="contained"
                            disabled={action === "update"}
                            onClick={handleUpdate}
                        >
                            {action === "update" ? <CircularProgress size={20} color="inherit" /> : "Update Role"}
                        </Button>
                        <Button fullWidth variant="outlined" color="inherit" onClick={() => setOpen("")}>
                            Cancel
                        </Button>
                    </Stack>
                </DialogContent>
            </Dialog>

            {/* ── Delete Confirm Dialog ───────────────────────────── */}
            <Dialog open={open === "delete"} fullWidth maxWidth="xs" onClose={() => setOpen("")}>
                <DialogContent>
                    <Box textAlign="center">
                        <Warning sx={{ width: 80, height: 80 }} color="error" />
                        <Typography color="error" fontSize={20} fontWeight={600} mt={1}>
                            Are you sure?
                        </Typography>
                        <Typography color="grey" fontSize={12} mb={2}>
                            This will permanently delete&nbsp;
                            <strong>{selectedRole?.fl_name}</strong>. This action cannot be undone.
                        </Typography>
                    </Box>
                    <Stack spacing={1}>
                        <Button
                            variant="contained"
                            color="error"
                            disabled={action === "delete"}
                            onClick={handleDelete}
                        >
                            {action === "delete" ? <CircularProgress size={20} color="inherit" /> : "Yes, Delete"}
                        </Button>
                        <Button variant="outlined" color="inherit" onClick={() => setOpen("")}>
                            Cancel
                        </Button>
                    </Stack>
                </DialogContent>
            </Dialog>

            <Container>
                {/* ── Page Header ─────────────────────────────────── */}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            Franchise Roles
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage franchise staff roles and their access details
                        </Typography>
                    </Box>
                    <Button variant="contained" startIcon={<Add />} onClick={openAdd}>
                        Add Role
                    </Button>
                </Stack>

                {/* ── Cards ───────────────────────────────────────── */}
                {loading ? (
                    <Box textAlign="center" py={6}>
                        <CircularProgress />
                    </Box>
                ) : roles.length === 0 ? (
                    <Alert severity="info">No roles found.</Alert>
                ) : (
                    <Grid container spacing={2}>
                        {roles.map((row) => (
                            <Grid item xs={12} sm={6} md={4} key={row.fl_id}>
                                <Card variant="outlined" sx={{ height: "100%" }}>
                                    <CardContent>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                            <Box>
                                                {/* Name */}
                                                <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                                                    <Person fontSize="small" color="action" />
                                                    <Typography fontWeight={600}>{row.fl_name}</Typography>
                                                </Stack>

                                                {/* Email */}
                                                <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                                                    <MailOutline fontSize="small" color="action" />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {row.fl_email}
                                                    </Typography>
                                                </Stack>

                                                {/* Role */}
                                                {row.fl_role && (
                                                    <Chip
                                                        label={row.fl_role.charAt(0).toUpperCase() + row.fl_role.slice(1)}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ mb: 0.5 }}
                                                    />
                                                )}
                                            </Box>
                                            {/* Status chip */}
                                            <Stack direction="row" justifyContent="flex-end" mb={1}>
                                                <Chip
                                                    label={
                                                        row.fl_status.charAt(0).toUpperCase() +
                                                        row.fl_status.slice(1)
                                                    }
                                                    size="small"
                                                    color={row.fl_status === "active" ? "success" : "default"}
                                                />
                                            </Stack>
                                        </Stack>

                                        <Divider sx={{ my: 1 }} />

                                        {/* ID & Date */}
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            mb={1.5}
                                        >
                                            <Typography variant="caption" color="text.secondary">
                                                {row.fl_id}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(row.fl_created_at).toLocaleDateString("en-IN", {
                                                    day: "2-digit",
                                                    month: "short",
                                                    year: "numeric",
                                                })}
                                            </Typography>
                                        </Stack>

                                        {/* Actions */}
                                        <Stack direction="row" spacing={1}>
                                            <Button
                                                size="small"
                                                fullWidth
                                                variant="outlined"
                                                startIcon={<Edit />}
                                                onClick={() => openEdit(row)}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                size="small"
                                                fullWidth
                                                variant="outlined"
                                                color="error"
                                                startIcon={<Delete />}
                                                onClick={() => openDelete(row)}
                                            >
                                                Delete
                                            </Button>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Container>
        </Box>
    );
}
